import React, { useState, useEffect, useRef } from 'react';
import { getArabicDate } from '../../services/utils/date';
import authService from '../../services/api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Camera, 
  Save, 
  Edit2, 
  X, 
  ShieldCheck, 
  GraduationCap, 
  BadgeCheck,
  Clock,
  Sparkles,
  Video,
  Target,
  Lightbulb,
  Youtube,
  MapPin,
  Image as ImageIcon,
  Upload,
  ArrowLeft
} from 'lucide-react';
import { useToast } from '../../components/common/Toast';
import { getImageUrl } from '../../config/apiConfig';

const MyProfile = () => {
  const { updateUser, user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [videoThumbnailPreview, setVideoThumbnailPreview] = useState(null);
  const [introVideoPreview, setIntroVideoPreview] = useState(null);
  const [introVideoFileName, setIntroVideoFileName] = useState(null);
  const [isRequestingVerification, setIsRequestingVerification] = useState(false);
  const [governorates, setGovernorates] = useState([]);
  const toast = useToast();

  // File input refs for safe access
  const avatarInputRef = useRef(null);
  const videoThumbnailInputRef = useRef(null);
  const introVideoInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      father_phone: user?.fatherPhone || '',
      address: user?.governorateId ? user.governorateId.toString() : '',
      bio: user?.bio || '',
      education: user?.education || '',
      specialization: user?.specialization || '',
      experienceYears: user?.experienceYears || '',
      qualifications: user?.qualifications || '',
      teachingMethods: user?.teachingMethods || '',
      introVideo: user?.introVideo || '',
    }
  });

  // تحديث النموذج فور توفر أو تغير بيانات المستخدم
  useEffect(() => {
    if (user && governorates.length > 0) {
      const addressValue = user.governorateId ? user.governorateId.toString() : '';
      reset({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        father_phone: user.fatherPhone || '',
        address: addressValue,
        bio: user.bio || '',
        education: user.education || '',
        specialization: user.specialization || '',
        experienceYears: user.experienceYears || '',
        qualifications: user.qualifications || '',
        teachingMethods: user.teachingMethods || '',
        introVideo: user.introVideo || '',
      });
    }
  }, [user, reset, governorates]);

  // جلب قائمة المحافظات
  useEffect(() => {
    const fetchGovernorates = async () => {
      try {
        const response = await authService.getGovernorates();
        if (response && response.governorates) {
          setGovernorates(response.governorates);
        }
      } catch (error) {
        // Failed to fetch governorates
      }
    };
    fetchGovernorates();
  }, []);

  // Diagnostics for avatar image
  useEffect(() => {
    if (user?.avatar) {
    }
  }, [user?.avatar]);

  // تنفيذ عملية تحديث الملف الشخصي (Mutation) مع دعم رفع ملفات متعددة
  const updateProfileMutation = useMutation(
    async (profileData) => {
      const formData = new FormData();
      
      Object.keys(profileData).forEach(key => {
        if (profileData[key] !== undefined && profileData[key] !== null) {
          formData.append(key, profileData[key]);
        }
      });

      const response = await authService.updateProfile(formData);
      return response;
    },
    {
      onSuccess: (data) => {
        updateUser(data.user);
        queryClient.invalidateQueries('auth-profile');
        queryClient.invalidateQueries(['user', user?.id]);
        toast.success('تم تحديث ملفك الشخصي بنجاح!');
        setIsEditing(false);
        setAvatarPreview(null);
        setVideoThumbnailPreview(null);
        setIntroVideoPreview(null);
        setIntroVideoFileName(null);
        
        // تنظيف refs
        if (avatarInputRef.current) avatarInputRef.current.value = '';
        if (videoThumbnailInputRef.current) videoThumbnailInputRef.current.value = '';
        if (introVideoInputRef.current) introVideoInputRef.current.value = '';
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل تحديث الملف الشخصي، يرجى المحاولة لاحقاً.');
      }
    }
  );

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) return toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) return toast.error('حجم الغلاف يجب أن يكون أقل من 5 ميجابايت');
      const reader = new FileReader();
      reader.onloadend = () => setVideoThumbnailPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleIntroVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // التحقق من نوع الملف
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
      if (!videoTypes.includes(file.type)) {
        return toast.error('صيغة الفيديو غير مدعومة. استخدم MP4 أو WebM أو OGG');
      }
      
      // التحقق من حجم الملف
      if (file.size > 200 * 1024 * 1024) {
        return toast.error('حجم الفيديو يجب أن يكون أقل من 200 ميجابايت');
      }
      
      // إنشاء معاينة آمنة وحفظ اسم الملف
      const objectUrl = URL.createObjectURL(file);
      setIntroVideoPreview(objectUrl);
      setIntroVideoFileName(file.name);
      toast.success(`تم تحديد الفيديو: ${file.name}`);
    }
  };

  const onSubmit = (data) => {
    // Safe file access using refs
    const avatarFile = avatarInputRef.current?.files?.[0];
    const videoThumbnailFile = videoThumbnailInputRef.current?.files?.[0];
    const introVideoFile = introVideoInputRef.current?.files?.[0];
    
    // إذا كان هناك ملف فيديو جديد، لا نرسل الـ introVideo النصي (YouTube URL)
    const profileData = { 
      ...data,
      // حذف رابط YouTube إذا تم رفع ملف فيديو جديد
      ...(introVideoFile && { introVideo: undefined }),
      // إضافة الملفات
      ...(avatarFile && { avatar: avatarFile }),
      ...(videoThumbnailFile && { videoThumbnail: videoThumbnailFile }),
      ...(introVideoFile && { introVideo: introVideoFile })
    };
    
    updateProfileMutation.mutate(profileData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAvatarPreview(null);
    setVideoThumbnailPreview(null);
    setIntroVideoPreview(null);
    setIntroVideoFileName(null);
    
    // تنظيف refs
    if (avatarInputRef.current) avatarInputRef.current.value = '';
    if (videoThumbnailInputRef.current) videoThumbnailInputRef.current.value = '';
    if (introVideoInputRef.current) introVideoInputRef.current.value = '';
    
    reset(); // يعيد القيم إلى defaultValues (بيانات اليوزر الحالية)
  };

  const handleRequestEmailVerification = async () => {
    if (isRequestingVerification) return;

    try {
      setIsRequestingVerification(true);
      // الباك إند يعتمد على التوكن لمعرفة المستخدم، لذلك لا نحتاج لإرسال بيانات إضافية
      await authService.requestVerification({});
      toast.success('تم إرسال رابط تفعيل البريد الإلكتروني إلى بريدك.');
    } catch (error) {
      const message = error.response?.data?.error || 'فشل في إرسال رابط التفعيل، حاول مرة أخرى.';
      toast.error(message);
    } finally {
      setIsRequestingVerification(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'مدير النظام المعتمَد';
      case 'teacher': return 'مدرس معتمد بالمنصة';
      case 'student': return 'طالب مسجل بالمنصة';
      default: return role;
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-10 bg-gray-50/30 min-h-screen">
      
      {/* Header & Role Info */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <div className="bg-primary-600 text-white p-1.5 rounded-xl shadow-lg shadow-primary-200"><User size={24} /></div>
                ملفي الشخصي
            </h1>
            <p className="text-gray-500 font-medium mr-12 hidden sm:block">تحكم في هويتك الرقمية ومعلوماتك المهنية والتعليمية</p>
        </div>
        <div className="flex items-center gap-3">
            {!isEditing ? (
                <button
                    onClick={() => setIsEditing(true)}
                    className="bg-white border border-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-black shadow-sm hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all flex items-center gap-2 transform active:scale-95"
                >
                    <Edit2 size={18} /> تعديل الملف
                </button>
            ) : (
                <div className="flex gap-2">
                    <button
                        onClick={handleCancel}
                        className="bg-red-50 text-red-600 px-5 py-3 rounded-2xl font-black text-xs hover:bg-red-600 hover:text-white transition-all flex items-center gap-2"
                    >
                        <X size={16} /> إلغاء
                    </button>
                </div>
            )}
        </div>
      </div>

      {/* Profile Hero Card - Layout Fix Applied */}
      <div className="bg-white rounded-[2.5rem] sm:rounded-[3.5rem] shadow-2xl shadow-gray-200/40 border border-gray-100 overflow-hidden relative group">
        <div className="absolute top-0 left-0 w-full h-32 sm:h-40 bg-gradient-to-r from-primary-700 via-primary-600 to-indigo-800"></div>
        <div className="absolute top-0 left-0 w-full h-32 sm:h-40 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        
        <div className="px-6 sm:px-10 lg:px-16 pb-12">
            <div className="relative flex flex-col md:flex-row gap-8 items-center md:items-end -mt-16 sm:-mt-20">
                {/* Avatar Center */}
                <div className="relative group z-20">
                    <div className="w-32 h-32 sm:w-44 h-44 rounded-[2.2rem] bg-white border-[8px] border-white shadow-2xl flex items-center justify-center text-primary-600 overflow-hidden transition-transform duration-500 group-hover:scale-105">
                        {avatarPreview ? (
                            <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                        ) : user?.avatar ? (
                            <img src={getImageUrl(user.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-primary-50 flex items-center justify-center"><User size={54} className="opacity-30" /></div>
                        )}
                    </div>
                    
                    {isEditing && (
                        <label className="absolute -bottom-2 -left-2 bg-primary-600 text-white p-3 rounded-2xl cursor-pointer hover:bg-primary-700 shadow-xl shadow-primary-100 transition-all transform hover:scale-110 active:scale-90 border-4 border-white z-30">
                            <Camera size={20} />
                            <input ref={avatarInputRef} type="file" name="avatar" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                        </label>
                    )}
                </div>

                {/* Fixed Name/Role Info Area - Correct Padding for Large Screens */}
                <div className="flex-1 space-y-2 mb-2 text-center md:text-right pt-4 md:pt-24 lg:pt-36">
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                        <h2 className="text-3xl sm:text-4xl font-black text-gray-900 leading-tight">{user?.name || 'مستخدم المنصة'}</h2>
                        <div className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border flex items-center gap-1.5 shadow-sm ${
                            user?.role === 'admin' ? 'bg-purple-50 text-purple-600 border-purple-100' : 
                            user?.role === 'teacher' ? 'bg-blue-50 text-blue-600 border-blue-100' : 
                            'bg-emerald-50 text-emerald-600 border-emerald-100'
                        }`}>
                            <ShieldCheck size={14} /> {getRoleLabel(user?.role)}
                        </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center justify-center md:justify-start text-gray-400 font-bold text-xs gap-6 tracking-tighter">
                        <div className="flex items-center gap-1.5 hover:text-primary-600 transition-colors"><Mail size={14} className="text-primary-500" /> {user?.email}</div>
                        {user?.address && <div className="flex items-center gap-1.5"><MapPin size={14} className="text-primary-500" /> {user?.address}</div>}
                        {user?.role === 'student' && user?.gradeName && <div className="flex items-center gap-1.5"><GraduationCap size={14} className="text-primary-500" /> {user?.gradeName}</div>}
                        <div className="flex items-center gap-1.5 uppercase"><Calendar size={14} className="text-primary-500" /> عضو منذ {user?.createdAt ? getArabicDate(user.createdAt) : 'حديثاً'}</div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-20">
        
        {/* Main Info Form (8 Columns) */}
        <div className="lg:col-span-8 space-y-8">
          
          {/* Section: Basic Identity */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 transition-all hover:shadow-lg">
            <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                <BadgeCheck size={22} className="text-primary-600" /> معلومات الحساب الأساسية
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">الاسم الكامل الظاهر للجميع *</label>
                    <div className="relative group">
                        <input
                            {...register('name', { required: 'الاسم مطلوب' })}
                            disabled={!isEditing}
                            className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                                !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                            }`}
                        />
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                    {errors.name && <p className="text-[10px] font-bold text-red-500 px-2">{errors.name.message}</p>}
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">رقم تليفون الشخصي</label>
                    <div className="relative group">
                        <input
                            {...register('phone')}
                            type="tel"
                            disabled={!isEditing}
                            placeholder="+020 --- --- ----"
                            className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                                !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                            }`}
                        />
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                </div>

                {user?.role === 'student' && (
                  <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">رقم ولي الأمر</label>
                      <div className="relative group">
                          <input
                              {...register('father_phone')}
                              type="tel"
                              disabled={!isEditing}
                              placeholder="+020 --- --- ----"
                              className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                                  !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                              }`}
                          />
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                      </div>
                  </div>
                )}

                <div className="space-y-2 md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">العنوان / المحافظة</label>
                    <select
                        {...register('address', { required: 'يرجى تحديد المحافظة' })}
                        disabled={!isEditing}
                        className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                            !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                        }`}
                    >
                        <option value="">اختر المحافظة</option>
                        {governorates.map(gov => (
                            <option key={gov.id} value={gov.id.toString()}>
                                {gov.name_ar}
                            </option>
                        ))}
                    </select>
                    {errors.address && <p className="text-[10px] font-bold text-red-500 px-2">{errors.address.message}</p>}
                </div>

                <div className="space-y-2 md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">نبذة تعريفية مختصرة</label>
                    <textarea
                      {...register('bio')}
                      rows={4}
                      disabled={!isEditing}
                      placeholder="اكتب شيئاً عن نفسك لتعريف زملائك ومدرسيك بك..."
                      className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed ${
                        !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                      }`}
                    />
                </div>
            </div>
          </div>

          {/* Section: Professional (Only for Teachers) */}
          {user?.role === 'teacher' && (
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 transition-all hover:shadow-lg animate-in slide-in-from-bottom-4 duration-700">
                <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                    <GraduationCap size={22} className="text-primary-600" /> الملف المهني للمدرس
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">المؤهل العلمي الأساسي</label>
                    <div className="relative group">
                      <input
                        {...register('education')}
                        disabled={!isEditing}
                        placeholder="بكالوريوس في..."
                        className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                          !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                        }`}
                      />
                      <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">التخصص الدقيق</label>
                    <div className="relative group">
                      <input
                        {...register('specialization')}
                        disabled={!isEditing}
                        placeholder="خبير في مادة..."
                        className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                          !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                        }`}
                      />
                      <Target className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">سنوات الخبرة التعليمية</label>
                    <div className="relative group">
                      <input
                        {...register('experienceYears')}
                        type="number"
                        disabled={!isEditing}
                        className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                          !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                        }`}
                      />
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">أساليب التدريس المتبعة</label>
                    <div className="relative group">
                      <input
                        {...register('teachingMethods')}
                        disabled={!isEditing}
                        placeholder="تعلم نشط، خرائط ذهنية..."
                        className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 ${
                          !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                        }`}
                      />
                      <Lightbulb className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">الفيديو التعريفي - رفع الملف (الخيار المفضل)</label>
                    
                    {/* Bunny Video Upload Section */}
                    <div className="space-y-3 p-4 bg-blue-50 rounded-2xl border-2 border-blue-100">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className={`inline-flex items-center justify-center w-full px-5 py-4 bg-white rounded-xl border-2 border-dashed border-blue-300 transition-all group ${!isEditing ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-blue-50'}`}>
                            <div className="text-center">
                              <Upload className={`mx-auto mb-2 text-blue-600 transition-transform ${isEditing ? 'group-hover:scale-110' : ''}`} size={24} />
                              <span className="text-xs font-black text-blue-600">اختر فيديو (حتى 200 ميجا)</span>
                            </div>
                            <input ref={introVideoInputRef} disabled={!isEditing} type="file" name="introVideo" accept="video/mp4,video/webm,video/ogg,video/quicktime" onChange={handleIntroVideoChange} className="hidden" />
                          </label>
                          {!isEditing && (
                            <p className="text-[10px] font-bold text-gray-400 text-center mt-2">اضغط "تعديل الملف" لتفعيل رفع الفيديو</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Video Preview and Info */}
                      {introVideoPreview && (
                        <div className="space-y-2 p-3 bg-white rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-black text-gray-600">✓ الملف المحدد:</p>
                              <p className="text-xs font-bold text-blue-600 truncate">{introVideoFileName}</p>
                            </div>
                            <video src={introVideoPreview} className="w-16 h-16 rounded-lg object-cover border border-blue-300" controls />
                          </div>
                        </div>
                      )}
                      
                      <p className="text-[10px] font-bold text-blue-600 text-center">سيتم رفع الفيديو إلى Bunny CDN للتشغيل السريع</p>
                    </div>

                    {/* YouTube URL Alternative (Legacy) */}
                    <div className="pt-2 space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">أو استخدم رابط YouTube (قديم)</label>
                      <div className="relative group">
                        <input
                          {...register('introVideo')}
                          disabled={!isEditing || !!introVideoPreview}
                          placeholder="https://www.youtube.com/watch?v=..."
                          className={`w-full px-5 py-4 bg-gray-50 border rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-800 text-xs ${
                            !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                          } ${introVideoPreview ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        <Youtube className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                      </div>
                      <p className="text-[10px] font-bold text-gray-400">ملاحظة: رفع الفيديو يعطل رابط YouTube</p>
                    </div>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">المؤهلات والشهادات الإضافية</label>
                    <textarea
                        {...register('qualifications')}
                        disabled={!isEditing}
                        rows={4}
                        placeholder="اذكر الشهادات التدريبية أو الجوائز التي حصلت عليها..."
                        className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed ${
                            !isEditing ? 'border-transparent cursor-not-allowed opacity-80' : 'border-gray-100'
                        }`}
                    />
                  </div>
                </div>
            </div>
          )}

          {/* Action Buttons Container */}
          {isEditing && (
                <div className="flex flex-col sm:flex-row justify-end gap-4 animate-in slide-in-from-bottom-2 duration-300">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="px-10 py-5 rounded-[1.8rem] font-black text-sm text-gray-400 hover:bg-gray-100 transition-all"
                    >
                        تجاهل التغييرات
                    </button>
                    <button
                        type="submit"
                        disabled={updateProfileMutation.isLoading}
                        className="bg-primary-600 text-white px-12 py-5 rounded-[1.8rem] font-black shadow-2xl shadow-primary-200 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {updateProfileMutation.isLoading ? <LoadingSpinner size="small" /> : <Save size={20} />}
                        حفظ التعديلات النهائية
                    </button>
                </div>
            )}
        </div>

        {/* Sidebar Info (4 Columns) */}
        <div className="lg:col-span-4 space-y-8">
          
          {/* Account Status Card */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 transition-all hover:shadow-xl">
            <h3 className="text-lg font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                <ShieldCheck size={20} className="text-primary-600" /> حالة الأمان
            </h3>
            
            <div className="space-y-4">
                <div className="p-5 rounded-3xl bg-gray-50/50 border border-gray-100 flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${user?.isActive ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]' : 'bg-red-500'}`} />
                        <span className="text-sm font-black text-gray-700 uppercase">النشاط</span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{user?.isActive ? 'حساب نشط' : 'معطل'}</span>
                </div>

                <div className="p-5 rounded-3xl bg-gray-50/50 border border-gray-100 flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${user?.emailVerified ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.4)]' : 'bg-amber-500 animate-pulse'}`} />
                        <span className="text-sm font-black text-gray-700 uppercase">التوثيق</span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        {user?.emailVerified ? 'مـوثـق' : 'بانتظار التأكيد'}
                      </span>
                      {!user?.emailVerified && (
                        <button
                          type="button"
                          onClick={handleRequestEmailVerification}
                          disabled={isRequestingVerification}
                          className="text-[10px] font-black text-primary-600 hover:text-primary-700 underline-offset-4 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isRequestingVerification ? 'جاري إرسال رابط التفعيل...' : 'إرسال رابط تفعيل إلى بريدك'}
                        </button>
                      )}
                    </div>
                </div>
            </div>
          </div>

          {/* Teacher Intro Media Control (Only for Teachers) */}
          {user?.role === 'teacher' && (
              <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 transition-all hover:shadow-xl">
                  <h3 className="text-lg font-black text-gray-900 mb-8 flex items-center gap-3 uppercase tracking-tighter">
                      <Video size={20} className="text-primary-600" /> غلاف الفيديو
                  </h3>
                  
                  <div className="space-y-6">
                    <div className="relative group/thumb aspect-video rounded-3xl overflow-hidden bg-gray-100 border-2 border-gray-50 shadow-inner">
                        
                        {videoThumbnailPreview ? (
                            <img src={videoThumbnailPreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : user?.videoThumbnail ? (
                            <img src={getImageUrl(user.videoThumbnail)} alt="Thumbnail" className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                                <ImageIcon size={32} strokeWidth={1} />
                                <span className="text-[10px] font-bold uppercase tracking-widest">No Cover Image</span>
                            </div>
                        )}
                        
                        {isEditing && (
                            <label className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity cursor-pointer">
                                <div className="bg-white p-3 rounded-2xl shadow-xl transform group-hover/thumb:scale-110 transition-transform">
                                    <Upload size={24} className="text-primary-600" />
                                </div>
                                <input ref={videoThumbnailInputRef} type="file" name="videoThumbnail" accept="image/*" onChange={handleVideoThumbnailChange} className="hidden" />
                            </label>
                        )}
                    </div>
                    <p className="text-[10px] font-bold text-gray-400 text-center uppercase tracking-tighter px-4 leading-relaxed">
                        يتم عرض هذه الصورة كخلفية للفيديو التعريفي في ملفك العام أمام الطلاب
                    </p>
                  </div>
              </div>
          )}

          {/* Tips Card - Role-Based */}
          {user?.role === 'teacher' && (
            <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative z-10 space-y-6">
                  <div className="bg-blue-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-900/50">
                      <Sparkles size={24} />
                  </div>
                  <h4 className="text-xl font-black mb-2 leading-tight tracking-tight">كلمتك لطلابك!</h4>
                  <p className="text-blue-100 text-sm font-medium leading-relaxed mb-8">
                      ملفك الشخصي المكتمل والمدعم بفيديو تعريفي يزيد من ثقة الطلاب بنسبة <span className="text-white font-black underline decoration-blue-400 underline-offset-4">85%</span> عن الملفات العادية.
                  </p>
                  <button 
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2 text-blue-300 font-black text-xs uppercase tracking-[0.2em] hover:text-white transition-colors"
                  >
                      تحديث بياناتك الآن <ArrowLeft size={16} />
                  </button>
              </div>
            </div>
          )}

          {user?.role === 'student' && (
            <div className="bg-gradient-to-br from-primary-900 to-primary-800 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative z-10 space-y-6">
                  <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl shadow-primary-900/50">
                      <Target size={24} />
                  </div>
                  <h4 className="text-xl font-black mb-2 leading-tight tracking-tight">حقق أهدافك التعليمية!</h4>
                  <p className="text-primary-100 text-sm font-medium leading-relaxed mb-8">
                      ملفك الشخصي المكتمل يساعدك على متابعة تقدمك الدراسي والتواصل مع معلميك بسهولة. <span className="text-white font-black">ابدأ الآن!</span>
                  </p>
                  <button 
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2 text-primary-300 font-black text-xs uppercase tracking-[0.2em] hover:text-white transition-colors"
                  >
                      إكمال بيانات ملفك <ArrowLeft size={16} />
                  </button>
              </div>
            </div>
          )}

          {user?.role === 'admin' && (
            <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
              <div className="relative z-10 space-y-6">
                  <div className="bg-purple-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl shadow-purple-900/50">
                      <ShieldCheck size={24} />
                  </div>
                  <h4 className="text-xl font-black mb-2 leading-tight tracking-tight">لوحة التحكم الإدارية</h4>
                  <p className="text-purple-100 text-sm font-medium leading-relaxed mb-8">
                      أنت تملك صلاحيات إدارية كاملة. يمكنك إدارة المستخدمين والدورات والمحتوى من خلال <span className="text-white font-black">لوحة التحكم</span>.
                  </p>
                  <button 
                      type="button"
                      onClick={() => window.location.href = '/dashboard'}
                      className="flex items-center gap-2 text-purple-300 font-black text-xs uppercase tracking-[0.2em] hover:text-white transition-colors"
                  >
                      الذهاب إلى لوحة التحكم <ArrowLeft size={16} />
                  </button>
              </div>
            </div>
          )}
        </div>

      </form>
    </div>
  );
};

export default MyProfile;